import { useEffect, useState } from "react";

/**
 * Live countdown to the 30-minute order-cancellation deadline.
 *  - Recomputed every second from `createdAt`.
 *  - Renders `mm:ss` in Sri Durga logo red.
 *  - Calls `onExpire()` exactly once when remaining hits zero.
 *  - Pass `windowMinutes` to change the eligibility window (default 30).
 */
export default function CancellationTimer({
  createdAt,
  windowMinutes = 30,
  onExpire,
  className = "",
  testId,
}) {
  const [remaining, setRemaining] = useState(() => computeRemaining(createdAt, windowMinutes));

  useEffect(() => {
    setRemaining(computeRemaining(createdAt, windowMinutes));
    const id = setInterval(() => {
      setRemaining((prev) => {
        const next = computeRemaining(createdAt, windowMinutes);
        if (prev > 0 && next <= 0 && onExpire) onExpire();
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [createdAt, windowMinutes, onExpire]);

  if (remaining <= 0) return null;

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <span
      className={`font-mono font-semibold text-brand-chili tabular-nums ${className}`}
      data-testid={testId}
      aria-label={`Cancellation window: ${mm} minutes ${ss} seconds remaining`}
    >
      {mm}:{ss}
    </span>
  );
}

function computeRemaining(createdAt, windowMinutes) {
  if (!createdAt) return 0;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return 0;
  const deadline = created + windowMinutes * 60 * 1000;
  return Math.max(0, Math.floor((deadline - Date.now()) / 1000));
}

/** Helper exposed to other components — computes initial eligibility w/o mounting.
 *  When `windowEnabled` is false the 30-minute deadline is ignored and the
 *  order is cancellable as long as it hasn't been dispatched / delivered /
 *  already cancelled / refunded.
 *
 *  iter51 — whitelist approach: only `placed` and `confirmed` orders can be
 *  cancelled by the customer. Every other state (packed, shipped, delivered,
 *  cancelled, return_requested/approved/rejected, refund_initiated,
 *  refund_completed, returned, payment_failed, payment_cancelled) hides the
 *  cancellation row entirely so the layout stays clean and no stale
 *  "Cancel order" button appears after a refund completes.
 */
const CUSTOMER_CANCELLABLE_STATUSES = new Set(["placed", "confirmed"]);

export function isCancellable(order, windowMinutes = 30, windowEnabled = true) {
  if (!order) return false;
  if (!CUSTOMER_CANCELLABLE_STATUSES.has(order.order_status)) return false;
  if (!windowEnabled) return true;
  return computeRemaining(order.created_at, windowMinutes) > 0;
}
